import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Wrench, TrendingUp, CheckCircle, XCircle, AlertTriangle, Star, Info } from "lucide-react";

export default function ExpertRecommendation({ predictiveData, carDetails, engineRating }) {
  if (!predictiveData) return null;

  const recommendation = predictiveData.recommendation || "";
  const warranty = predictiveData.warranty_assessment || {};
  const repairEstimate = predictiveData.repair_estimate || {};
  const marketValue = predictiveData.market_value || {};

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden" data-testid="expert-recommendation-card">
      <div className="border-b border-slate-200 p-4 bg-slate-900 flex items-center gap-2">
        <ShieldCheck size={18} className="text-orange-400" />
        <span className="text-sm font-bold uppercase tracking-wider text-white">MFCWL Expert Assessment</span>
      </div>
      <CardContent className="p-6">
        {/* Main Recommendation */}
        <div className="p-4 bg-slate-50 rounded-sm border border-slate-100 mb-5">
          <p className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-2">Engine Condition Summary</p>
          <p className="text-base text-slate-700 leading-relaxed">{recommendation}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Extended Warranty Eligibility */}
          <div className="space-y-3" data-testid="warranty-section">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1">
              <ShieldCheck size={12} /> Extended Warranty Options
            </p>
            <div className={`p-4 rounded-sm border ${
              warranty.eligible
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                {warranty.eligible
                  ? <CheckCircle size={20} className="text-emerald-500" />
                  : <XCircle size={20} className="text-red-500" />
                }
                <span className={`text-base font-bold ${warranty.eligible ? 'text-emerald-700' : 'text-red-700'}`}>
                  {warranty.eligible ? "Eligible for Coverage" : "Not Eligible"}
                </span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">{warranty.reason}</p>
              
              {/* 3 Warranty Options */}
              {warranty.eligible && warranty.warranty_options && (
                <div className="space-y-2 mt-3">
                  {warranty.warranty_options.map((opt, i) => (
                    <div 
                      key={i} 
                      className={`p-3 rounded-sm border ${
                        opt.recommended 
                          ? 'bg-emerald-100 border-emerald-300 ring-1 ring-emerald-400' 
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${opt.recommended ? 'text-emerald-800' : 'text-slate-700'}`}>
                            {opt.tier}
                          </span>
                          {opt.recommended && (
                            <Badge className="bg-emerald-600 text-white text-sm px-1.5 py-0">
                              <Star size={8} className="mr-0.5" /> RECOMMENDED
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm font-bold font-mono text-slate-900">{opt.price}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span>{opt.duration}</span>
                        <span>•</span>
                        <span>{opt.km_limit}</span>
                      </div>
                      <p className="text-sm text-slate-400 mt-1">{opt.coverage}</p>
                    </div>
                  ))}
                </div>
              )}

              {warranty.conditions && warranty.conditions.length > 0 && (
                <div className="mt-3 space-y-1.5 pt-2 border-t border-slate-200">
                  {warranty.conditions.map((c, i) => (
                    <p key={i} className="text-sm text-slate-500 flex items-start gap-1.5">
                      <AlertTriangle size={10} className="mt-0.5 text-amber-500 flex-shrink-0" />
                      {c}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Estimated Repair Costs - Now with Minor & Major */}
          <div className="space-y-3" data-testid="repair-estimate-section">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1">
              <Wrench size={12} /> Repair Cost Estimates
            </p>
            <div className="p-4 bg-slate-50 rounded-sm border border-slate-100">
              {/* Minor Repairs - Maintenance */}
              {repairEstimate.minor_repairs && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-emerald-600 uppercase tracking-widest font-bold flex items-center gap-1">
                      <CheckCircle size={10} /> {repairEstimate.minor_repairs.label || "Recommended Maintenance"}
                    </span>
                    <span className="text-base font-bold text-emerald-700 font-mono">{repairEstimate.minor_repairs.total}</span>
                  </div>
                  <p className="text-sm text-slate-400 mb-2">{repairEstimate.minor_repairs.description}</p>
                  <div className="space-y-1 bg-white rounded-sm p-2 border border-slate-100">
                    {repairEstimate.minor_repairs.items?.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">{item.item}</span>
                        <span className="font-mono text-slate-600">{item.cost}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Major Repairs - If Issues Persist */}
              {repairEstimate.major_repairs && repairEstimate.major_repairs.total_usd > 0 && (
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-amber-600 uppercase tracking-widest font-bold flex items-center gap-1">
                      <AlertTriangle size={10} /> {repairEstimate.major_repairs.label || "Major Repairs"}
                    </span>
                    <span className="text-base font-bold text-amber-700 font-mono">{repairEstimate.major_repairs.total}</span>
                  </div>
                  <p className="text-sm text-slate-400 mb-2">{repairEstimate.major_repairs.description}</p>
                  <div className="space-y-1 bg-amber-50 rounded-sm p-2 border border-amber-100">
                    {repairEstimate.major_repairs.items?.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">{item.item}</span>
                        <span className="font-mono text-slate-600">{item.cost}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendation */}
              {repairEstimate.recommendation && (
                <div className="mt-3 p-2 bg-blue-50 rounded-sm border border-blue-100">
                  <p className="text-sm text-blue-700 flex items-start gap-1.5">
                    <Info size={12} className="mt-0.5 flex-shrink-0" />
                    {repairEstimate.recommendation}
                  </p>
                </div>
              )}

              {/* Urgency Badge */}
              {repairEstimate.urgency && (
                <Badge className={`mt-3 rounded-full text-sm font-bold uppercase tracking-widest border ${
                  repairEstimate.urgency === 'critical' ? 'bg-red-100 text-red-800 border-red-200' :
                  repairEstimate.urgency === 'immediate' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                  repairEstimate.urgency === 'soon' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                  repairEstimate.urgency === 'scheduled' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                  'bg-emerald-100 text-emerald-800 border-emerald-200'
                }`}>
                  {repairEstimate.urgency === 'critical' ? 'Critical - Immediate' :
                   repairEstimate.urgency === 'immediate' ? 'Needs Attention' :
                   repairEstimate.urgency === 'soon' ? 'Service Soon' :
                   repairEstimate.urgency === 'scheduled' ? 'Scheduled Service' :
                   'Routine Maintenance'}
                </Badge>
              )}

              {/* Legacy fallback for old format */}
              {!repairEstimate.minor_repairs && repairEstimate.breakdown && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-slate-400 uppercase tracking-widest font-bold">Total Estimate</span>
                    <span className="text-xl font-bold text-slate-900 font-mono">{repairEstimate.total_cost || "N/A"}</span>
                  </div>
                  <div className="space-y-2 border-t border-slate-200 pt-3">
                    {repairEstimate.breakdown.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">{item.item}</span>
                        <span className="font-mono text-slate-700 font-bold">{item.cost}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Market Value */}
          <div className="space-y-3" data-testid="market-value-section">
            <p className="text-sm font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1">
              <TrendingUp size={12} /> Fair Market Value
            </p>
            <div className="p-4 bg-slate-50 rounded-sm border border-slate-100">
              {marketValue.country && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-400 uppercase tracking-widest font-bold">
                    Market: {marketValue.country}
                  </span>
                  <span className="text-sm font-mono text-slate-400">{marketValue.currency}</span>
                </div>
              )}
              {marketValue.fair_price && (
                <div className="text-center p-3 bg-white rounded-sm border border-slate-200 mb-3">
                  <p className="text-sm text-slate-400 uppercase tracking-widest font-bold">Estimated Value</p>
                  <p className="text-2xl font-bold text-slate-900 font-mono" data-testid="market-fair-price">{marketValue.fair_price}</p>
                  {marketValue.value_retention && (
                    <p className="text-sm text-slate-400 mt-1">{marketValue.value_retention}</p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                {marketValue.price_range_low && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Price Range Low</span>
                    <span className="font-mono text-slate-600">{marketValue.price_range_low}</span>
                  </div>
                )}
                {marketValue.price_range_high && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Price Range High</span>
                    <span className="font-mono text-slate-600">{marketValue.price_range_high}</span>
                  </div>
                )}
                {marketValue.condition_adjustment && (
                  <div className="flex justify-between text-sm border-t border-slate-200 pt-2 mt-2">
                    <span className="text-slate-400">Engine Condition</span>
                    <span className={`font-mono font-bold ${parseFloat(marketValue.condition_adjustment) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {marketValue.condition_adjustment}
                    </span>
                  </div>
                )}
                {marketValue.depreciation_pct && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Depreciation</span>
                    <span className="font-mono text-slate-500">{marketValue.depreciation_pct}</span>
                  </div>
                )}
              </div>
              {marketValue.condition_note && (
                <p className="text-sm text-slate-400 mt-3 leading-relaxed italic">{marketValue.condition_note}</p>
              )}
              {marketValue.notes && (
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{marketValue.notes}</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
